import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CATALOGUE_ENABLED } from '../config/features';
import { ArrowRight, Calendar, MapPin, Users, Clock } from '../components/icons';
import { events, getEventById } from '../data/events';

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = id ? getEventById(id) : undefined;
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setActiveImage(0);
  }, [id]);

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center pt-24">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Event not found</h1>
        <p className="text-gray-500 mb-6">The event you’re looking for doesn’t exist or has been moved.</p>
        <Link to="/events" className="inline-flex items-center gap-2 bg-[#0D56A6] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0A4A8F] transition-colors">
          Back to Events <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const related = events.filter(e => e.id !== event.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section data-hero className={`relative overflow-hidden ${event.upcoming ? 'pt-32 pb-12' : 'h-[60vh] min-h-[420px]'}`}>
        {event.upcoming ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D56A6] to-[#062f63]" />
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#8EC641]/10 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
          </>
        ) : (
          <>
            <img src={event.heroImage} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
          </>
        )}

        <div className="relative z-10 h-full flex flex-col justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-[#8EC641] text-white text-xs font-bold px-3 py-1 rounded-full">{event.category}</span>
            <span className="text-white/80 text-sm">{event.phase}</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight"
          >
            {event.title}
          </motion.h1>
          <p className="text-white/70 mt-2 italic">{event.partner}</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Designed promo banner (upcoming events), shown uncropped */}
            {event.upcoming && event.bannerImage && (
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
                <img src={event.bannerImage} alt={event.title} className="w-full aspect-video object-cover" />
              </div>
            )}

            {/* Image gallery (past events with photos) */}
            {!event.upcoming && event.gallery.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="aspect-[16/10] bg-gray-100">
                  <img src={event.gallery[activeImage]} alt={event.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                {event.gallery.length > 1 && (
                  <div className="p-4 flex gap-3 overflow-x-auto">
                    {event.gallery.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                          activeImage === i ? 'border-[#0D56A6] ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img src={img} alt={`${event.title} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Programme (upcoming events) */}
            {event.upcoming && (event.morning || event.afternoon) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Programme</h2>
                {event.programmeNote && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-6 mt-3 inline-block">
                    {event.programmeNote}
                  </p>
                )}

                {event.morning && (() => {
                  const hasTimes = event.morning.some((r) => r.time && r.time.trim());
                  return (
                  <div className="mb-8">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#0D56A6] mb-3">Schedule</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="text-left text-gray-500 border-b border-gray-200">
                            {hasTimes && <th className="py-2 pr-4 font-semibold whitespace-nowrap">Time</th>}
                            <th className="py-2 pr-4 font-semibold">Session</th>
                            <th className="py-2 font-semibold whitespace-nowrap">Speaker</th>
                          </tr>
                        </thead>
                        <tbody>
                          {event.morning.map((row, i) => (
                            <tr key={i} className="border-b border-gray-100 align-top">
                              {hasTimes && <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">{row.time}</td>}
                              <td className="py-2.5 pr-4 text-gray-900 font-medium">{row.session}</td>
                              <td className="py-2.5 text-gray-600 whitespace-nowrap">{row.speaker || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  );
                })()}

                {event.afternoon && (
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#0D56A6] mb-1">Afternoon · Hands-on Workshops</h3>
                    <p className="text-sm text-gray-500 mb-3">Delegates split into two groups and swap at 15:00, so everyone attends both workshops.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="text-left text-gray-500 border-b border-gray-200">
                            <th className="py-2 pr-4 font-semibold whitespace-nowrap">Time</th>
                            <th className="py-2 pr-4 font-semibold">Group 1</th>
                            <th className="py-2 font-semibold">Group 2</th>
                          </tr>
                        </thead>
                        <tbody>
                          {event.afternoon.map((row, i) => (
                            <tr key={i} className="border-b border-gray-100 align-top">
                              <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">{row.time}</td>
                              <td className="py-2.5 pr-4 text-gray-900">{row.group1}</td>
                              <td className="py-2.5 text-gray-900">{row.group2}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {event.capacityNote && (
                  <p className="text-sm text-gray-500 mt-6 pt-6 border-t border-gray-100">
                    <span className="font-semibold text-gray-700">Capacity: </span>{event.capacityNote}
                  </p>
                )}
              </div>
            )}

            {/* About */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About this event</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{event.description}</p>
              <p className="text-gray-600 leading-relaxed">{event.longDescription}</p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Event highlights</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {event.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-[#8EC641]/15 flex items-center justify-center">
                      <svg className="w-3 h-3 text-[#8EC641]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </span>
                    <span className="text-gray-700 text-sm">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:sticky lg:top-24">
            {/* Details card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Event details</h3>
              <div className="space-y-4">
                {[
                  { icon: <Calendar size={18} className="text-[#0D56A6]" />, label: 'Date', value: event.date },
                  { icon: <Clock size={18} className="text-[#0D56A6]" />, label: 'Time', value: event.time },
                  { icon: <MapPin size={18} className="text-[#0D56A6]" />, label: 'Location', value: event.venueNote ? `${event.location} · ${event.venueNote}` : event.location },
                  { icon: <Users size={18} className="text-[#0D56A6]" />, label: event.upcoming ? 'Capacity' : 'Attendees', value: event.upcoming ? `${event.attendees} delegates` : `${event.attendees} professionals` },
                ].map((row) => (
                  <div key={row.label} className="flex items-start gap-3">
                    <span className="mt-0.5 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">{row.icon}</span>
                    <div>
                      <p className="text-xs text-gray-400">{row.label}</p>
                      <p className="text-sm font-medium text-gray-900">{row.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                {event.upcoming && event.registrationOpen ? (
                  <Link to={`/events/${event.id}/register`} className="w-full inline-flex items-center justify-center gap-2 bg-[#8EC641] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#7ab535] transition-colors">
                    Register Now <ArrowRight size={16} />
                  </Link>
                ) : (
                  <Link to="/contact" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D56A6] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#0A4A8F] transition-colors">
                    Register Interest <ArrowRight size={16} />
                  </Link>
                )}
                <Link to="/events" className="w-full inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] font-semibold px-6 py-3 rounded-lg hover:bg-[#0D56A6] hover:text-white transition-colors">
                  All Events
                </Link>
              </div>
            </div>

            {/* Product highlight (hidden while the Catalogue is in development) */}
            {CATALOGUE_ENABLED && (
            <div className="bg-gradient-to-br from-[#0D56A6] to-[#062f63] rounded-2xl shadow-sm p-6 text-white">
              <p className="text-white/60 text-xs uppercase tracking-wide mb-1">Featured Product</p>
              <p className="text-2xl font-bold mb-2">{event.product}</p>
              <p className="text-white/80 text-sm mb-4">Learn more about the products featured in this event.</p>
              <Link to="/catalogue" className="inline-flex items-center gap-2 bg-[#8EC641] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#7ab535] transition-colors text-sm">
                Browse Catalogue <ArrowRight size={14} />
              </Link>
            </div>
            )}
          </div>
        </div>

        {/* Related events */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Other events</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((e) => (
                <Link
                  key={e.id}
                  to={`/events/${e.id}`}
                  className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                    <img src={e.heroImage} alt={e.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-[#8EC641]/15 text-[#6fa02c] text-xs font-semibold px-2.5 py-0.5 rounded-full">{e.category}</span>
                      <span className="text-xs text-gray-400">{e.date}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 group-hover:text-[#0D56A6] transition-colors">{e.title}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{e.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
